/**
 * Read-only left pane for the annotation + adjudication flows.
 *
 * Renders:
 *   - record id (monospace)
 *   - source-metadata badges (channel pill, journey, product, interaction date)
 *   - verbatim parsed into customer/agent turns via `parseVerbatimTurns`
 *
 * Per `feature-spec.md` §3.4.1. Will be reused by B11's adjudication flow.
 */
import React from 'react';

import { Badge } from '../../../../../ui/badge';
import { Card } from '../../../../../ui/card';
import { CHANNELS } from '../../../shared/channels';
import type { DatasetRecord } from '../../types';
import { parseVerbatimTurns, type VerbatimTurn } from './verbatim';

interface Props {
  record: DatasetRecord;
}

const Turn: React.FC<{ turn: VerbatimTurn }> = ({ turn }) => {
  // Non-transcript prose (`speaker: 'none'`) renders as a plain
  // paragraph without a speech bubble or speaker chip — a social
  // post or pasted complaint isn't actually said by anyone.
  if (turn.speaker === 'none') {
    return (
      <div
        className="text-[13px] leading-relaxed text-foreground whitespace-pre-wrap"
        data-testid="verbatim-turn-none"
      >
        {turn.text}
      </div>
    );
  }
  const isCustomer = turn.speaker === 'customer';
  return (
    <div
      className={`flex flex-col ${isCustomer ? 'items-start' : 'items-end'}`}
      data-testid={`verbatim-turn-${turn.speaker}`}
    >
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {isCustomer ? 'Customer' : 'Agent'}
      </div>
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-[13px] leading-relaxed ${
          isCustomer
            ? 'rounded-tl-sm bg-muted text-foreground'
            : 'rounded-tr-sm bg-primary/10 text-foreground'
        }`}
      >
        {turn.text}
      </div>
    </div>
  );
};

const RecordContextPane: React.FC<Props> = ({ record }) => {
  const turns = parseVerbatimTurns(record.verbatim);
  const channelMeta = CHANNELS[record.sourceMetadata.channel as keyof typeof CHANNELS];

  return (
    <Card
      className="flex h-full min-h-0 flex-col overflow-hidden p-0"
      data-testid="record-context-pane"
    >
      <div className="border-b border-border bg-background px-4 py-3">
        <div className="flex items-baseline justify-between gap-2">
          <span className="font-mono text-[12px] text-muted-foreground">{record.id}</span>
          <span className="text-[11px] text-muted-foreground">{record.interactionDate}</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {channelMeta && (
            <Badge
              variant="outline"
              style={{
                borderColor: `${channelMeta.color}40`,
                color: channelMeta.color,
              }}
            >
              {channelMeta.name}
            </Badge>
          )}
          {record.sourceMetadata.journey && (
            <Badge variant="secondary">{record.sourceMetadata.journey}</Badge>
          )}
          {record.sourceMetadata.product && (
            <Badge variant="secondary">{record.sourceMetadata.product}</Badge>
          )}
        </div>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {turns.length === 0 ? (
          <p className="text-[13px] italic text-muted-foreground">
            (No verbatim text.)
          </p>
        ) : (
          turns.map((turn, idx) => <Turn key={idx} turn={turn} />)
        )}
      </div>
    </Card>
  );
};

export default RecordContextPane;
